"use client";

import { DatePicker } from "@/components/ui/datepicker";
import { routes } from "@/config/routes";
import {
  EDiscountType,
  EPromotionStatus,
  EPromotionType,
  EUsageLimitType,
  ICreatePromotionDTO,
  IPromotion,
} from "@/utilities/types/Promotion";
import {
  createPromotionSchema,
  updatePromotionSchema,
} from "@/utilities/validators/promotion";
import cn from "@/utils/class-names";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { PiCurrencyDollarBold, PiPercentBold, PiXBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Input,
  Select,
  SelectOption,
  Textarea,
  Title,
} from "rizzui";

export interface PromotionFormProps {
  initialData?: IPromotion | null;
  isLoading: boolean;
  onSubmit: (data: ICreatePromotionDTO) => Promise<void>;
  isUpdate?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export default function PromotionForm({
  initialData,
  isLoading,
  onSubmit,
  isUpdate = false,
  onClose,
  isModal = false,
}: PromotionFormProps) {
  const navigate = useNavigate();
  const title = isUpdate ? "Edit Promotion" : "Create Promotion";

  const form = useForm<ICreatePromotionDTO>({
    resolver: zodResolver(
      isUpdate ? updatePromotionSchema : createPromotionSchema
    ),
    defaultValues: {
      code: "",
      description: "",
      discount: 1,
      discountType: EDiscountType.PERCENT,
      status: EPromotionStatus.ACTIVE,
      startDate: undefined,
      endDate: undefined,
      usageLimitType: EUsageLimitType.PER_PROMO_CODE,
      usageLimit: 1,
      type: EPromotionType.PACKAGE,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, errors },
    getValues,
  } = form;

  // Watch discountType for changes to update discount value and prefix/suffix
  const discountType = useWatch({
    control,
    name: "discountType",
  });

  useEffect(() => {
    if (initialData) {
      reset({
        description: initialData.description || "",
        discount: initialData.discount ? Number(initialData.discount) : 0,
        discountType: initialData.discountType || EDiscountType.PERCENT,
        status: initialData.status || EPromotionStatus.ACTIVE,
        startDate: initialData.startDate
          ? new Date(initialData.startDate)
          : undefined,
        endDate: initialData.endDate
          ? new Date(initialData.endDate)
          : undefined,
        usageLimitType:
          initialData.usageLimitType || EUsageLimitType.PER_PROMO_CODE,
        usageLimit: initialData.usageLimit || 1,
        type: initialData.type || EPromotionType.PACKAGE,
        code: initialData.code || "",
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = async (data: ICreatePromotionDTO) => {
    try {
      // If updating, we need to preserve the original values for fields that can't be updated
      if (isUpdate && initialData) {
        const updatedData = {
          ...data,
        };
        await onSubmit(updatedData);
      } else {
        await onSubmit(data);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Something went wrong";
      toast.error(errorMessage);
    }
  };

  // Handle date range selection
  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setValue("startDate", start ? start : undefined, { shouldValidate: true });
    setValue("endDate", end ? end : undefined, { shouldValidate: true });
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="grid grid-cols-1 gap-6 p-6 @container  [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
      noValidate
    >
      <div className="col-span-full flex items-center justify-between">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          {title}
        </Title>
        {isModal && onClose && (
          <ActionIcon size="sm" variant="text" onClick={onClose}>
            <PiXBold className="h-auto w-5" />
          </ActionIcon>
        )}
      </div>

      <Controller
        name="code"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            label="Promo code"
            placeholder="Enter promo code"
            disabled={isUpdate}
            {...field}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(/\s+/g, "");
              field.onChange(sanitizedValue);
            }}
            className="[&>label>span]:font-medium col-span-full"
            error={error?.message}
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Textarea
            label="Description"
            placeholder="Enter promotion description"
            disabled={isLoading}
            {...field}
            className="[&>label>span]:font-medium col-span-full"
            error={error?.message}
          />
        )}
      />

      <div className={cn("grid col-span-full gap-4 grid-cols-4 items-end")}>
        <Controller
          name="discountType"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              label="Discount Type"
              options={[
                { label: "Percent", value: EDiscountType.PERCENT },
                { label: "Fixed Amount", value: EDiscountType.PRICE },
              ]}
              placeholder="Select discount type"
              value={field.value}
              onChange={(option: SelectOption) => {
                field.onChange(option.value);
              }}
              disabled={isUpdate}
              displayValue={(value) =>
                value === EDiscountType.PERCENT ? "Percent" : "Fixed Amount"
              }
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
            />
          )}
        />

        <Controller
          name="discount"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Discount"
              type="number"
              placeholder="Enter discount amount"
              disabled={isUpdate}
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
              suffix={
                discountType === EDiscountType.PERCENT ? (
                  <PiPercentBold className="h-4 w-4 text-gray-500" />
                ) : (
                  <PiCurrencyDollarBold className="h-4 w-4 text-gray-500" />
                )
              }
            />
          )}
        />

        <Controller
          name="usageLimitType"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              label="Usage Limit"
              options={[
                {
                  label: "PER PROMO CODE",
                  value: EUsageLimitType.PER_PROMO_CODE,
                },
                { label: "PER USER", value: EUsageLimitType.PER_USER },
              ]}
              placeholder="Select Usage Limit"
              value={field.value}
              onChange={(option: SelectOption) => {
                field.onChange(option.value);
              }}
              disabled={isLoading}
              displayValue={(value) =>
                value === EUsageLimitType.PER_PROMO_CODE
                  ? "PER PROMO CODE"
                  : "PER USER"
              }
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
            />
          )}
        />
        <Controller
          name="usageLimit"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Usage Limit"
              type="number"
              placeholder="Enter usage limit"
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
            />
          )}
        />
      </div>

      <div className="col-span-full">
        <div className="mb-1.5 font-medium text-gray-900">Date Range</div>
        <DatePicker
          selectsRange
          startDate={getValues("startDate")}
          endDate={getValues("endDate")}
          onChange={handleDateRangeChange}
          dateFormat="dd MMM, yyyy"
          placeholderText="Select date range"
          popperPlacement="bottom-start"
          disabled={isLoading}
          inputProps={{
            variant: "text",
            inputClassName: "p-0 px-1 h-auto [&_input]:text-ellipsis",
            error: errors.startDate?.message || errors.endDate?.message,
          }}
          className="rounded border [&_.rizzui-input-error-text]:p-1 [&_.rizzui-input-container]:px-3 [&_.rizzui-input-container]:py-1.5 w-full"
        />
      </div>

      <div
        className={cn(
          "col-span-full grid gap-4",
          isUpdate ? "md:grid-cols-1" : "md:grid-cols-2"
        )}
      >
        {!isUpdate && (
          <Controller
            name="type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label="Promotion Type"
                options={[{ label: "Package", value: EPromotionType.PACKAGE }]}
                placeholder="Select promotion type"
                value={field.value}
                onChange={(option: SelectOption) => {
                  field.onChange(option.value);
                }}
                disabled={isLoading}
                displayValue={(value) =>
                  value === EPromotionType.PACKAGE ? "Package" : ""
                }
                className="[&>label>span]:font-medium col-span-full md:col-span-1"
                error={error?.message}
              />
            )}
          />
        )}
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              label="Status"
              options={[
                { label: "Active", value: EPromotionStatus.ACTIVE },
                { label: "Inactive", value: EPromotionStatus.DEACTIVE },
              ]}
              placeholder="Select status"
              value={field.value}
              onChange={(option: SelectOption) => {
                field.onChange(option.value);
              }}
              disabled={isLoading}
              displayValue={(value) =>
                value === EPromotionStatus.ACTIVE ? "Active" : "Inactive"
              }
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
            />
          )}
        />
      </div>

      <div className="col-span-full flex items-center justify-end gap-4">
        <Button
          variant="outline"
          onClick={
            isModal && onClose
              ? onClose
              : () => navigate(routes.promotion.listing)
          }
          className="md:w-auto w-1/2"
          type="button"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          className="md:w-auto w-1/2"
          disabled={isLoading || !isDirty}
        >
          {title}
        </Button>
      </div>
    </form>
  );
}
